import StyledButton from '@/components/styled/StyledButton';
import StyledContainer from '@/components/styled/StyledContainer';
import StyledFormHeaderWithBreadcrumb from '@/components/styled/StyledFormHeaderWithBreadcrumb';
import { useLocation, useNavigate } from 'react-router';
import ArrowLeftIcon from '@/assets/icons/arrow-left.svg?react';
import PlusIcon from '@/assets/icons/plus.svg?react';
import SearchIcon from '@/assets/icons/search.svg?react';
import { useGetAllNetworks } from '@/hooks/useDistributors';
import { useDebouncedSearch } from '@/hooks/useDebouncedSearch';
import StyledTable from '@/components/styled/StyledTable';
import { NETWORK_COLUMNS } from '@/types/distributors/networkColumnDef';
import { useGetNetworks } from '@/api/user.api';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import StyledSelect from '@/components/styled/StyledSelect';
import { useForm, useWatch } from 'react-hook-form';
import { LabelValue } from '@/types/global';
import StyledInput from '@/components/styled/StyledInput';
import { useMemo, useState } from 'react';
import AgenciesSubList from '@/components/distributors/AgenciesSubList';
import SelectCircuitsModal from '@/components/distributors/modals/SelectCircuitsModal';
import { RowSelectionState } from '@tanstack/react-table';
import CircuitSharedConfirmationModal from '@/components/distributors/modals/CircuitSharedConfirmationModal';
import clsx from 'clsx';
import RecentAgenciesGroupedByNetworks from '@/components/distributors/RecentAgenciesGroupedByNetworks';
import { useTranslation } from '@/i18n';
import { useTranslatedConst } from '@/hooks/useTranslatedConst';
import { PATHS } from '@/constants/Paths';

const AddDistributorsPage = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { t } = useTranslation();
  const getTranslatedConst = useTranslatedConst();
  const { control } = useForm<{
    search: string;
    network?: LabelValue<string>;
  }>({
    mode: 'onChange',
    defaultValues: {
      search: '',
      network: undefined,
    },
  });

  const searchValue = useWatch({ control, name: 'search' });
  const debouncedSearch = useDebouncedSearch(searchValue);
  const networkValue = useWatch({ control, name: 'network' });
  const filters = useMemo(
    () => ({
      networkName: debouncedSearch,
      networkId: networkValue?.value ?? '',
    }),
    [debouncedSearch, networkValue?.value],
  );

  const { data: networksWithAgencies, isSuccess } = useGetAllNetworks({
    networkId: filters.networkId || undefined,
    networkName: filters.networkName || undefined,
  });

  const { data: networkOptions } = useGetNetworks((networks) =>
    networks.map((network) => ({ label: network.name, value: network.id })),
  );

  const [isCircuitSelectionModalOpen, setIsCircuitSelectionModalOpen] =
    useState(false);
  const [
    isCircuitSharingConfirmationModalOpen,
    setIsCircuitSharingConfirmationModalOpen,
  ] = useState(false);

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [selectedAgencies, setSelectedAgencies] = useState<
    Record<string, string[]>
  >({});
  const [currentTab, setCurrentTab] = useState<
    'allDistributors' | 'recentlyAddedDistributors'
  >(state?.currentTab ?? 'allDistributors');

  const allSelectedAgencies = useMemo(
    () =>
      Object.keys(selectedAgencies)
        .filter((networkId) => !(networkId in rowSelection))
        .map((networkId) => selectedAgencies[networkId])
        .flat(),
    [rowSelection, selectedAgencies],
  );

  const selectedNetworks = useMemo(
    () => Object.keys(rowSelection),
    [rowSelection],
  );

  return (
    <StyledContainer>
      <StyledFormHeaderWithBreadcrumb
        sticky
        title={t('distributors.addDistributors')}
        breadcrumb={
          <StyledButton
            variant="link"
            title={t('distributors.backToList')}
            className="text-sm text-[#3D3D3D] pl-0"
            onClick={() => navigate(PATHS.home.distributors.list)}
            icon={<ArrowLeftIcon />}
          />
        }
      >
        <StyledButton
          title={t('distributors.cancel')}
          onClick={() => navigate(PATHS.home.distributors.list)}
          variant="outlined"
          className="font-semibold gap-1 px-3 py-2"
        />
        <StyledButton
          title={t('distributors.selectCircuits')}
          onClick={() => {
            setIsCircuitSelectionModalOpen(true);
          }}
          disabled={
            allSelectedAgencies.length === 0 && selectedNetworks.length === 0
          }
          className="font-semibold gap-1 px-3 py-2"
          icon={<PlusIcon className="size-5 fill-white" />}
        />
      </StyledFormHeaderWithBreadcrumb>
      <div className="flex flex-col w-full h-auto p-6 gap-6">
        <Tabs defaultValue={currentTab} className="w-full">
          <TabsList>
            <TabsTrigger
              value="allDistributors"
              className={clsx('hover:cursor-pointer', {
                hidden:
                  !isSuccess || networksWithAgencies.countNewAgencies === 0,
              })}
              onClick={() => {
                setRowSelection({});
                setSelectedAgencies({});
                setCurrentTab('allDistributors');
              }}
            >
              {t('distributors.tabs.allDistributors')}
            </TabsTrigger>
            {isSuccess && networksWithAgencies.countNewAgencies > 0 && (
              <TabsTrigger
                value="recentlyAddedDistributors"
                className="hover:cursor-pointer flex items-center justify-center gap-2"
                onClick={() => {
                  setRowSelection({});
                  setSelectedAgencies({});
                  setCurrentTab('recentlyAddedDistributors');
                }}
              >
                <span>{t('distributors.tabs.recentlyAddedDistributors')}</span>
                <div className="size-5 flex justify-center items-center text-brand-100 bg-brand-500 border border-brand-600 rounded-2xl px-1.5 py-0.5">
                  {networksWithAgencies.countNewAgencies}
                </div>
              </TabsTrigger>
            )}
          </TabsList>
          <div className="flex flex-col gap-6 w-full h-auto pt-4">
            <div className="flex flex-col gap-2">
              <span className="text-xs font-normal text-gray-600">
                {t('distributors.filterBy')}
              </span>
              <div className="flex flex-col md:flex-row justify-between gap-4">
                <div className="flex flex-col md:flex-row gap-4">
                  <StyledSelect
                    className="min-w-1/3 md:min-w-48 lg:min-w-60 xl:min-w-80"
                    control={control}
                    options={networkOptions ?? []}
                    name="network"
                    isClearable={true}
                    placeholder={t('distributors.networkPlaceholder')}
                  />
                </div>
                <div className="w-full md:w-1/3 lg:w-1/4 2xl:w-1/5">
                  <StyledInput
                    control={control}
                    name="search"
                    placeholder={t('distributors.quickSearchPlaceholder')}
                    icon={<SearchIcon className="w-5 h-5 stroke-gray-500" />}
                  />
                </div>
              </div>
            </div>
            <TabsContent value="allDistributors">
              <StyledTable
                columns={getTranslatedConst(NETWORK_COLUMNS)}
                data={networksWithAgencies?.networks ?? []}
                getRowId={(row) => row.id}
                isExpandable
                getRowCanExpand={(row) => row.original._count.Agency > 0}
                expandedNode={(row) => (
                  <AgenciesSubList
                    networkId={row.original.id}
                    setSelectedAgencies={setSelectedAgencies}
                    shouldSelectAll={selectedNetworks.includes(row.original.id)}
                    key={`agencies-list-${row.original.id}`}
                  />
                )}
                onRowClick={(row) => {
                  row.toggleExpanded();
                }}
                setSelection={setRowSelection}
              />
              <div className="flex flex-row justify-between items-center gap-3 pl-6">
                <span className="text-sm leading-6 text-gray-700 font-medium">
                  {isSuccess && networksWithAgencies.networks.length > 0
                    ? networksWithAgencies.networks.length === 1
                      ? t('distributors.counts.oneNetwork')
                      : t('distributors.counts.multipleNetworks', {
                          count: networksWithAgencies.networks.length,
                        })
                    : t('distributors.noDistributorNetworksFound')}
                </span>
              </div>
            </TabsContent>
          </div>
          {isSuccess && networksWithAgencies.countNewAgencies > 0 && (
            <TabsContent value="recentlyAddedDistributors">
              <RecentAgenciesGroupedByNetworks
                filters={filters}
                setRowSelection={setRowSelection}
                setSelectedAgencies={setSelectedAgencies}
                selectedNetworks={selectedNetworks}
              />
            </TabsContent>
          )}
        </Tabs>
      </div>
      {isCircuitSelectionModalOpen && (
        <SelectCircuitsModal
          agencies={allSelectedAgencies}
          closeModal={() => {
            setIsCircuitSelectionModalOpen(false);
          }}
          onSuccess={() => {
            setIsCircuitSharingConfirmationModalOpen(true);
          }}
          shouldShareFutureCircuitsWithAllSelectedAgencies={false}
          networks={selectedNetworks}
        />
      )}
      {isCircuitSharingConfirmationModalOpen && (
        <CircuitSharedConfirmationModal
          closeModal={() => setIsCircuitSharingConfirmationModalOpen(false)}
        />
      )}
    </StyledContainer>
  );
};

export default AddDistributorsPage;
